import React, { useState, useContext } from "react";

import { useMutation, useApolloClient } from "react-apollo";

import { Field, Control, Icon, Tab, Button, Title } from "rbx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { useLocation, Link, Route, Switch, useHistory } from "react-router-dom";
import { useLocalStorage } from "../../../utils/hooks";
import {
  CREATE_VEHICLE_MUTATION,
  ALL_VEHICLES_QUERY
} from "../../../graphql/Vehicles/Vehicles.graphql";
import { ModalContext } from "../../../components/ModalProvider";
import Profile from "./routes/Profile";
import { getUTCFormattedDate, ourToast as toast } from "../../../utils";
import { useAuthorization } from "../../../components/Authorization";
import Loading from "../../../components/Loading";

const INITIAL_STATE = {
  VehicleNumber: "",
  Description: null,
  VehicleTypeRecIdRef: null,
  StatusRecIdRef: null,
  CategoryRecIdRef: null,
  CompanyOwned: false,
  Year: null,
  Make: null,
  Model: null,
  SerialNumber: null,
  Engine: null,
  Axles: null,
  TransmissionRecIdRef: null,
  RearRecIdRef: null,
  BodyRecIdRef: null,
  NumDieselTanks: null,
  TireSizeRecIdRef: null,
  WheelSizeRecIdRef: null,
  MaxCold: null,
  MaxWarm: null,
  PlateNumber: null,
  RegistrationNumber: null,
  RegistrationState: null,
  RegistrationExpirationDate: null,
  OverWeightPermit: null,
  Unladen: null,
  Payload: null,
  TrycockNet: null,
  CompanyId: null,
  RecId: null,
  Odometer: null,
  ELDVehicleId: null
};

function NewVehiclePage() {
  const location = useLocation();
  const client = useApolloClient();
  const AdminAuth = useAuthorization(["ADMIN"]);
  const FleetAuth = useAuthorization(["FLEET"]);
  const [isLoading, setLoading] = useState(false);
  const { setModalOpen } = useContext(ModalContext);
  const history = useHistory();

  const [createVehicle, res] = useMutation(CREATE_VEHICLE_MUTATION);

  const [inputs, setInputs, clearInputs] = useLocalStorage(
    "NEW_VEHICLE",
    INITIAL_STATE
  );

  const handleChange = e => {
    const value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setInputs({ ...inputs, [e.target.name]: value });
  };

  const handleSave = async goTo => {
    try {
      const obj = Object.keys(INITIAL_STATE).reduce(
        (acc, curr) =>
          inputs[curr] !== null ? { ...acc, [curr]: inputs[curr] } : acc,
        {}
      );
      const data = {
        ...obj,
        VehicleTypeRecIdRef: parseInt(inputs.VehicleTypeRecIdRef),
        StatusRecIdRef: parseInt(inputs.StatusRecIdRef),
        CategoryRecIdRef: parseInt(inputs.CategoryRecIdRef),
        Axles: parseInt(inputs.Axles),
        TransmissionRecIdRef: parseInt(inputs.TransmissionRecIdRef),
        RearRecIdRef: parseInt(inputs.RearRecIdRef),
        BodyRecIdRef: parseInt(inputs.BodyRecIdRef),
        NumDieselTanks: parseInt(inputs.NumDieselTanks),
        TireSizeRecIdRef: parseInt(inputs.TireSizeRecIdRef),
        MaxWarm: parseInt(inputs.MaxWarm),
        MaxCold: parseInt(inputs.MaxCold),
        Unladen: parseInt(inputs.Unladen),
        Payload: parseInt(inputs.Payload),
        TrycockNet: parseInt(inputs.TrycockNet),
        RegistrationExpirationDate: getUTCFormattedDate(
          inputs.RegistrationExpirationDate
        ),
        Year: parseInt(inputs.Year),
        Odometer: parseFloat(inputs.Odometer)
      };
      setLoading(true);
      const vehicleExists = await client.query({
        query: ALL_VEHICLES_QUERY,
        variables: {
          where: { VehicleNumber: data.VehicleNumber },
          first: 1
        }
      });
      if (
        vehicleExists &&
        vehicleExists.data &&
        Array.isArray(vehicleExists.data.Vehicles) &&
        vehicleExists.data.Vehicles.length > 0
      ) {
        toast.error("This vehicle number is being used by another vehicle.");
      } else {
        const create = await createVehicle({
          variables: {
            data
          }
        });
        if (!goTo && create?.data?.createVehicle) {
          const { RecId } = create?.data?.createVehicle;
          toast.success("Vehicle Created successfully.");
          history.push(`/fleet/vehicles/${RecId}/profile`);
        } else if (goTo && create?.data?.createVehicle) {
          const { RecId } = create?.data?.createVehicle;
          toast.success("Vehicle Created successfully.");
          history.push(`/fleet/vehicles/${RecId}/${goTo}`);
        }
        clearInputs();
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      console.log(err.message);
    }
  };

  const getActionButtons = () => (
    <Field>
      <Control>
        <Button color="success" size="small" onClick={() => handleSave(false)}>
          <Icon>
            <FontAwesomeIcon icon="save" />
          </Icon>
          <span>Save</span>
        </Button>
      </Control>
    </Field>
  );
  if (isLoading || isLoading || res?.loading) return <Loading />;

  return (
    <div className="vehicle-page">
      <header className="page-head">
        <div className="page-head-start">
          <Title color="primary" size={1}>
            New Vehicle
          </Title>
          <div className="page-head-start--subheader">
            <span>{`${inputs?.Description?.substr(0, 16) || ""}`}</span>
          </div>
        </div>
        <div className="page-head-tabs">
          <Tab.Group kind="boxed">
            <Tab active as={Button} onClick={() => handleSave("profile")}>
              <span>Profile</span>
            </Tab>
            <Tab as={Button} onClick={() => handleSave("ELD")}>
              <span>ELD</span>
            </Tab>
            <Tab as={Button} onClick={() => handleSave("inspections")}>
              <span>Inspections</span>
            </Tab>
            {(FleetAuth || AdminAuth) && (
              <React.Fragment>
                <Tab as={Button} onClick={() => handleSave("labor")}>
                  <span>Labor</span>
                </Tab>
                <Tab as={Button} onClick={() => handleSave("parts")}>
                  <span>Parts</span>
                </Tab>
              </React.Fragment>
            )}
            <Tab
              active={location.pathname.includes("o-o-s-history")}
              as={Link}
              tabIndex="-1"
              to="o-o-s-history"
            >
              <span>OOS History</span>
            </Tab>
          </Tab.Group>
        </div>
        <div className="page-head-end">{getActionButtons()}</div>
      </header>

      <Switch>
        <Route path="/fleet/vehicles/new">
          <Profile
            editing
            inputs={inputs}
            setModalOpen={setModalOpen}
            onChange={handleChange}
          />
        </Route>
      </Switch>
    </div>
  );
}
NewVehiclePage.propTypes = {};
export default NewVehiclePage;
